import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import { useI18n } from "@/context/LangContext";
import { WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Contact() {
  const ref = useReveal();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [openWa, setOpenWa] = useState(true);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.toast.error"));
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      toast.success(t("contact.toast.success"));
      if (openWa) {
        window.open(
          `https://wa.me/7054986771?text=${encodeURIComponent(`Hi Maxine, I just sent an enquiry — ${form.name}, ${form.company || "-"}. Message: ${form.message}`)}`,
          "_blank"
        );
      }
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" ref={ref} data-testid="contact" className="reveal section bg-[color:var(--paper-alt)]">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">{t("contact.eyebrow")}</div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.02]">
              {t("contact.title")}
            </h2>
            <p className="mt-6 text-neutral-700 leading-relaxed">
              {t("contact.sub")}
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
              {["b1", "b2", "b3", "b4"].map((k) => (
                <li key={k} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[color:var(--brand)]" /> {t(`contact.${k}`)}</li>
              ))}
            </ul>
            <div className="mt-10 space-y-4 border-t border-[color:var(--line)] pt-8">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("contact.whatsapp")}</div>
                <a href="https://wa.me/7054986771" target="_blank" rel="noreferrer" data-testid="contact-wa-link" className="text-xl font-display font-bold hover:text-[color:var(--brand)] transition-colors">+91 70549 86771</a>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("contact.email")}</div>
                <a href="mailto:support@maxineconsulting.in" data-testid="contact-email" className="text-xl font-display font-bold hover:text-[color:var(--brand)] transition-colors">support@maxineconsulting.in</a>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("contact.office")}</div>
                <div className="text-lg">Dubai Design District, Building 6</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 border border-[color:var(--line)] p-6 md:p-10 bg-white">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("contact.form.name")} value={form.name} onChange={handle("name")} testid="contact-name" required />
              <Field label={t("contact.form.email")} type="email" value={form.email} onChange={handle("email")} testid="contact-email-input" required />
              <Field label={t("contact.form.phone")} value={form.phone} onChange={handle("phone")} testid="contact-phone" />
              <Field label={t("contact.form.company")} value={form.company} onChange={handle("company")} testid="contact-company" />
            </div>
            <div className="mt-5">
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("contact.form.msg")}</label>
              <textarea
                data-testid="contact-message"
                rows={5}
                value={form.message}
                onChange={handle("message")}
                className="mt-2 w-full bg-white border border-[color:var(--line)] focus:border-[color:var(--brand)] outline-none px-4 py-3 placeholder:text-neutral-400"
                placeholder={t("contact.form.msg.ph")}
                required
              />
            </div>
            <label className="mt-5 flex items-center gap-3 text-sm text-neutral-700 select-none">
              <input type="checkbox" data-testid="contact-wa-toggle" checked={openWa} onChange={(e) => setOpenWa(e.target.checked)} className="accent-[color:var(--brand)]" />
              {t("contact.form.wa")}
            </label>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="btn-brand disabled:opacity-60"
              >
                <PaperPlaneTilt size={16} weight="bold" /> {sending ? t("contact.form.sending") : t("contact.form.submit")}
              </button>
              <a
                href={`https://wa.me/7054986771?text=${encodeURIComponent("Hi Maxine, I'd like to chat.")}`}
                target="_blank" rel="noreferrer" data-testid="contact-wa-direct"
                className="btn-ghost"
              >
                <WhatsappLogo size={16} weight="fill" /> {t("contact.form.chat")}
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}{required && " *"}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-white border border-[color:var(--line)] focus:border-[color:var(--brand)] outline-none px-4 py-3 placeholder:text-neutral-400"
      />
    </label>
  );
}
